import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { friendlyError } from "@/lib/friendlyError";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Loader } from "@/components/ui/Primitives";
import { ArrowLeft, ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * Landlord-only page — publish your own payment instructions so tenants
 * know where to send rent. Central Rentals never touches the money.
 */
export default function PaymentInstructions() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    interac_email: "",
    bank_details: "",
    other_link: "",
    mailing_address: "",
    notes: "",
  });

  useEffect(() => {
    if (user && user.role !== "landlord" && user.role !== "admin") {
      nav("/dashboard", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/rent/payment-instructions/mine");
        setForm({
          interac_email:   data?.interac_email   || "",
          bank_details:    data?.bank_details    || "",
          other_link:      data?.other_link      || "",
          mailing_address: data?.mailing_address || "",
          notes:           data?.notes           || "",
        });
      } catch { /* first-time — leave defaults */ }
      finally { setLoading(false); }
    })();
  }, [user, nav]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/rent/payment-instructions", form);
      toast.success("Payment instructions saved. Tenants can see them now.");
      nav("/payments");
    } catch (err) {
      toast.error(friendlyError(err, "Couldn't save your payment instructions."));
    } finally { setSaving(false); }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <Link to="/payments" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"
            data-testid="back-to-payments-link">
        <ArrowLeft className="w-4 h-4"/> Back to Payments
      </Link>
      <PageHeader
        title="Payment instructions"
        subtitle="Tell tenants where to send rent. You can list any combination — most Canadian landlords accept Interac e-Transfer."
      />

      <Card className="p-4 mb-5 bg-emerald-50 border-emerald-200 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center flex-none">
          <ShieldCheck className="w-4 h-4"/>
        </div>
        <div className="text-sm text-emerald-900">
          <div className="font-semibold">Central Rentals never touches your rent</div>
          <div className="text-emerald-800/85 mt-0.5">
            Tenants pay you directly using the details below. This app only records that
            the payment happened so both sides have a clean history.
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-5">
          <div>
            <label htmlFor="interac_email" className="block text-sm font-semibold text-slate-900 mb-1">
              Interac e-Transfer email
            </label>
            <p className="text-xs text-slate-500 mb-2">The email address tenants send the transfer to. Most Canadian banks auto-deposit if enabled.</p>
            <input
              id="interac_email" type="email"
              data-testid="input-interac-email"
              value={form.interac_email}
              onChange={(e) => setForm({ ...form, interac_email: e.target.value })}
              placeholder="you@example.com"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
          </div>

          <div>
            <label htmlFor="bank_details" className="block text-sm font-semibold text-slate-900 mb-1">
              Bank transfer details
            </label>
            <p className="text-xs text-slate-500 mb-2">Institution / transit / account number, or IBAN — whatever your bank supports.</p>
            <textarea
              id="bank_details" rows={3}
              data-testid="input-bank-details"
              value={form.bank_details}
              onChange={(e) => setForm({ ...form, bank_details: e.target.value })}
              placeholder="e.g. Institution 003 · Transit 12345 · Account 1234567"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
          </div>

          <div>
            <label htmlFor="other_link" className="block text-sm font-semibold text-slate-900 mb-1">
              Payment link (optional)
            </label>
            <p className="text-xs text-slate-500 mb-2">A PayPal.me, Wise, or invoicing link if you prefer that method.</p>
            <input
              id="other_link" type="url"
              data-testid="input-other-link"
              value={form.other_link}
              onChange={(e) => setForm({ ...form, other_link: e.target.value })}
              placeholder="https://paypal.me/yourname"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
          </div>

          <div>
            <label htmlFor="mailing_address" className="block text-sm font-semibold text-slate-900 mb-1">
              Cheque mailing address (optional)
            </label>
            <p className="text-xs text-slate-500 mb-2">Where tenants send a cheque if that's preferred.</p>
            <textarea
              id="mailing_address" rows={2}
              data-testid="input-mailing-address"
              value={form.mailing_address}
              onChange={(e) => setForm({ ...form, mailing_address: e.target.value })}
              placeholder={"e.g. 123 Main St\nToronto, ON  M5H 2N2"}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-semibold text-slate-900 mb-1">
              Notes for tenants (optional)
            </label>
            <p className="text-xs text-slate-500 mb-2">e.g. "Please include the unit number in the transfer memo."</p>
            <textarea
              id="notes" rows={2}
              data-testid="input-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything else your tenant should know…"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <Link to="/payments"><Button variant="secondary" data-testid="cancel-instructions-button">Cancel</Button></Link>
            <Button onClick={save} disabled={saving} data-testid="save-instructions-button">
              <Save className="w-4 h-4"/> {saving ? "Saving…" : "Save instructions"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
